<template>
  <transition name="banner-slide">
    <div
      v-if="showBanner"
      ref="banner"
      class="pn-onboarding-banner"
      :class="[bannerClass, { 'is-dark': isDark }]"
    >
      <div class="banner-inner">

        <div class="banner-icon-wrap" :class="iconWrapClass">
          <feather-icon :icon="bannerIcon" size="18" />
        </div>

        <div class="banner-text-wrap">
          <span class="banner-title">Cadastro pendente</span>
          <span class="banner-sep">·</span>
          <span class="banner-urgency" :class="urgencyTextClass">{{ daysLabel }}</span>
          <span class="banner-sep d-none d-md-inline">·</span>
          <span class="banner-detail d-none d-md-inline">
            {{ missingCount }} {{ missingCount === 1 ? 'item obrigatório em aberto' : 'itens obrigatórios em aberto' }}
          </span>
        </div>

        <div class="banner-actions">
          <b-button size="sm" :variant="actionVariant" class="banner-cta" @click="goToCompanyInfo">
            Completar agora
          </b-button>
          <button type="button" class="banner-close" aria-label="Fechar" @click="dismiss">
            <feather-icon icon="XIcon" size="14" />
          </button>
        </div>

      </div>
      <div class="banner-progress" :style="progressStyle" />
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { BButton } from 'bootstrap-vue'
import * as types from '@/modules/account/store/types'

const DISMISS_PREFIX = 'pn_banner_dismissed_'
const todayKey = (userId) => `${DISMISS_PREFIX}${new Date().toISOString().slice(0, 10)}_${userId}`

export default {
  name: 'PnOnboardingBanner',
  components: { BButton },
  data() {
    const userId = JSON.parse(localStorage.getItem('userData') || '{}')?.id || 'guest'
    return {
      dismissedToday: localStorage.getItem(todayKey(userId)) === '1',
      _savedPaddingTop: null,
      _paddingApplied: false,
      _userId: userId,
    }
  },
  computed: {
    ...mapGetters({
      isPN: types.IS_PN,
      user: types.USER,
      onboardingStatus: types.PN_ONBOARDING_STATUS,
    }),
    isDark() {
      return this.$store.state.appConfig?.layout?.skin === 'dark'
    },
    daysRemaining() {
      return this.onboardingStatus ? this.onboardingStatus.days_remaining : null
    },
    missingCount() {
      return this.onboardingStatus ? (this.onboardingStatus.missing_count || 0) : 0
    },
    showBanner() {
      return (
        this.isPN
        && !this.dismissedToday
        && this.daysRemaining !== null
        && this.daysRemaining >= 0
        && this.missingCount > 0
      )
    },
    daysLabel() {
      if (this.daysRemaining === 0) return 'Último dia!'
      if (this.daysRemaining === 1) return '1 dia restante'
      return `${this.daysRemaining} dias restantes`
    },
    bannerClass() {
      if (this.daysRemaining <= 1) return 'banner--critical'
      if (this.daysRemaining <= 3) return 'banner--warning'
      return 'banner--info'
    },
    bannerIcon() {
      if (this.daysRemaining <= 1) return 'AlertOctagonIcon'
      if (this.daysRemaining <= 3) return 'AlertTriangleIcon'
      return 'InfoIcon'
    },
    iconWrapClass() {
      if (this.daysRemaining <= 1) return 'icon--danger'
      if (this.daysRemaining <= 3) return 'icon--warning'
      return 'icon--primary'
    },
    urgencyTextClass() {
      if (this.daysRemaining <= 1) return 'urgency--danger'
      if (this.daysRemaining <= 3) return 'urgency--warning'
      return 'urgency--primary'
    },
    actionVariant() {
      if (this.daysRemaining <= 1) return 'danger'
      if (this.daysRemaining <= 3) return 'warning'
      return 'primary'
    },
    progressStyle() {
      const totalDays = parseInt(process.env.VUE_APP_DAYS_TO_SIGN_CONTRACT || '30')
      const pct = Math.min(100, ((totalDays - this.daysRemaining) / totalDays) * 100)
      const color = this.daysRemaining <= 1 ? 'var(--danger)'
        : this.daysRemaining <= 3 ? 'var(--warning)'
        : 'var(--primary)'
      return { width: `${pct}%`, backgroundColor: color }
    },
  },
  methods: {
    ...mapActions({ checkOnboarding: types.CHECK_PN_ONBOARDING_STATUS }),

    applyPositioning() {
      const bannerEl = this.$refs.banner
      const contentEl = document.querySelector('.content.app-content')
      const navbarEl  = document.querySelector('.header-navbar')

      if (!bannerEl) return

      // Pin banner directly below the actual rendered navbar
      if (navbarEl) {
        bannerEl.style.top = `${navbarEl.getBoundingClientRect().bottom}px`
      }

      // Push content down exactly once by the banner's rendered height
      if (contentEl && !this._paddingApplied) {
        const bannerH = bannerEl.getBoundingClientRect().height
        this._savedPaddingTop = contentEl.style.paddingTop
        const currentPt = parseFloat(window.getComputedStyle(contentEl).paddingTop) || 0
        contentEl.style.paddingTop = `${currentPt + bannerH}px`
        this._paddingApplied = true
      }
    },

    removePositioning() {
      const contentEl = document.querySelector('.content.app-content')
      if (contentEl && this._paddingApplied) {
        contentEl.style.paddingTop = this._savedPaddingTop || ''
        this._savedPaddingTop = null
        this._paddingApplied = false
      }
    },

    dismiss() {
      localStorage.setItem(todayKey(this._userId), '1')
      this.dismissedToday = true
    },
    goToCompanyInfo() {
      if (this.user && this.user.appify_consultant_id) {
        this.$router.push({
          name: 'consultant-company-info-save',
          params: { id: this.user.appify_consultant_id },
        })
      }
    },
  },
  watch: {
    showBanner(val) {
      this.$nextTick(() => {
        if (val) {
          this.applyPositioning()
        } else {
          this.removePositioning()
        }
      })
    },
  },
  created() {
    this.checkOnboarding()
  },
  mounted() {
    // Fallback: if showBanner is already true on mount (Vuex data already present)
    if (this.showBanner) {
      this.$nextTick(() => this.applyPositioning())
    }
  },
  beforeDestroy() {
    this.removePositioning()
  },
}
</script>

<style lang="scss" scoped>
// ─── Container ───────────────────────────────────────────────────────────────

.pn-onboarding-banner {
  position: fixed;
  // top is set dynamically in onEnter() to match actual navbar bottom
  top: 4.45rem;   // fallback before JS runs
  left: 0;
  right: 0;
  z-index: 1035;
  overflow: hidden;

  // Light mode defaults
  background-color: #ffffff;
  border-left: 5px solid var(--primary);
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.1);

  // Dark mode defaults
  &.is-dark {
    background-color: #283046;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.5);
  }

  // Warning — strong amber pulse
  &.banner--warning {
    border-left-color: var(--warning);
    animation: warn-light 2s ease-in-out infinite;

    &.is-dark {
      animation: warn-dark 2s ease-in-out infinite;
    }
  }

  // Critical — strong red pulse
  &.banner--critical {
    border-left-color: var(--danger);
    animation: crit-light 1.3s ease-in-out infinite;

    &.is-dark {
      animation: crit-dark 1.3s ease-in-out infinite;
    }
  }
}

// Light mode — warning: white → strong amber tint, border 5px → 13px
@keyframes warn-light {
  0%,100% { background-color: #ffffff;  border-left-width: 5px;  box-shadow: 0 3px 12px rgba(0,0,0,0.08); }
  50%      { background-color: #fff0cc; border-left-width: 13px; box-shadow: 0 3px 20px rgba(255,159,67,0.3); }
}

// Dark mode — warning
@keyframes warn-dark {
  0%,100% { background-color: #283046; border-left-width: 5px;  box-shadow: 0 3px 12px rgba(0,0,0,0.5); }
  50%      { background-color: #3a2c08; border-left-width: 13px; box-shadow: 0 3px 24px rgba(255,159,67,0.35); }
}

// Light mode — critical: white → strong red tint, border 5px → 16px
@keyframes crit-light {
  0%,100% { background-color: #ffffff;  border-left-width: 5px;  box-shadow: 0 3px 12px rgba(0,0,0,0.08); }
  50%      { background-color: #ffe0e0; border-left-width: 16px; box-shadow: 0 3px 28px rgba(234,84,85,0.45); }
}

// Dark mode — critical
@keyframes crit-dark {
  0%,100% { background-color: #283046; border-left-width: 5px;  box-shadow: 0 3px 12px rgba(0,0,0,0.5); }
  50%      { background-color: #3d1010; border-left-width: 16px; box-shadow: 0 3px 32px rgba(234,84,85,0.5); }
}

// ─── Inner row ───────────────────────────────────────────────────────────────

.banner-inner {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 1.25rem 0.6rem 1rem;
  flex-wrap: wrap;
}

// ─── Icon ────────────────────────────────────────────────────────────────────

.banner-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  flex-shrink: 0;

  &.icon--primary {
    color: var(--primary);
    background: rgba(115, 103, 240, 0.12);
    animation: icon-breathe 3s ease-in-out infinite;
  }
  &.icon--warning {
    color: var(--warning);
    background: rgba(255, 159, 67, 0.15);
    animation: icon-pulse 1.8s ease-in-out infinite;
  }
  &.icon--danger {
    color: var(--danger);
    background: rgba(234, 84, 85, 0.15);
    animation: icon-urgent 1s ease-in-out infinite;
  }
}

@keyframes icon-breathe {
  0%,100% { transform: scale(1); }
  50%      { transform: scale(1.1); }
}

@keyframes icon-pulse {
  0%,100% { transform: scale(1);     box-shadow: 0 0 0 0   rgba(255,159,67,0); }
  50%      { transform: scale(1.18); box-shadow: 0 0 0 7px rgba(255,159,67,0.25); }
}

@keyframes icon-urgent {
  0%,100% { transform: scale(1);    box-shadow: 0 0 0 0   rgba(234,84,85,0); }
  40%      { transform: scale(1.22); box-shadow: 0 0 0 9px rgba(234,84,85,0.3); }
  70%      { transform: scale(1.08); box-shadow: 0 0 0 4px rgba(234,84,85,0.12); }
}

// ─── Text ────────────────────────────────────────────────────────────────────

.banner-text-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem;
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  color: #3a3a4a;

  .is-dark & { color: #d0d2d6; }
}

.banner-title { font-weight: 600; }

.banner-sep {
  color: #c5c5d0;
  user-select: none;
  .is-dark & { color: #3e4460; }
}

.banner-urgency {
  font-weight: 700;
  &.urgency--primary { color: var(--primary); }
  &.urgency--warning { color: #c97800; }        // darkened for white bg contrast
  &.urgency--danger  { color: var(--danger);  }
}
.is-dark .banner-urgency.urgency--warning { color: var(--warning); }

.banner-detail { font-size: 0.82rem; opacity: 0.6; }

// ─── Actions ─────────────────────────────────────────────────────────────────

.banner-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: auto;
  flex-shrink: 0;
}

.banner-cta { font-weight: 600; white-space: nowrap; }

.banner-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  color: #888;
  transition: background 0.15s, color 0.15s;

  &:hover { background: rgba(0,0,0,0.07); color: #333; }

  .is-dark & { color: #8a8fa8; }
  .is-dark &:hover { background: rgba(255,255,255,0.08); color: #d0d2d6; }
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

.banner-progress {
  height: 2px;
  opacity: 0.55;
  transition: width 1s ease, background-color 0.4s ease;
}

// ─── Slide transition — spring bounce ────────────────────────────────────────

.banner-slide-enter-active {
  transition: transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease;
}
.banner-slide-leave-active {
  transition: transform 0.25s ease-in, opacity 0.2s ease;
}
.banner-slide-enter,
.banner-slide-leave-to {
  transform: translateY(-110%);
  opacity: 0;
}
</style>
